import React, { useCallback, useEffect } from 'react';
import NarrativeLesson from './NarrativeLesson';
import NarrativeStory from './NarrativeStory';
import { scrollToTop } from 'shared/utils/pageUtils';
import {
  StoryValue,
  LessonValue,
  NarrativeProps,
  parseStoryValue,
  parseLessonValue,
  NarrativeSubmitReference,
  NARRATIVE_STORY_QUESTION_ID,
  NARRATIVE_LESSON_QUESTION_ID,
} from './utils';
import { BottomBar } from '../BottomBar';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { GuidanceTextResponse } from '../utils';

export type FeedbackMakeEditsNarrativeHandler = React.ElementRef<
  typeof NarrativeStory | typeof NarrativeLesson
>;

const Narrative: React.FC<NarrativeProps & {
  guidanceText: GuidanceTextResponse;
  questionRef?: React.RefObject<NarrativeSubmitReference>;
}> = ({
  value,
  question,
  reportId,
  questionRef,
  isThirdParty,
  hideBottomBar,
  handleNext,
  handleBack,
  onPreviousSection,
  handleValueChange,
}) => {
  useEffect(() => {
    scrollToTop();
  }, [question.dbId]);

  const handleStoryChange = useCallback(
    (story: StoryValue) => {
      handleValueChange(question.dbId, { text: story });
    },
    [question.dbId, handleValueChange],
  );

  const handleLessonChange = useCallback(
    (lesson: LessonValue) => {
      handleValueChange(question.dbId, { text: lesson });
    },
    [question.dbId, handleValueChange],
  );

  const onNext = useCallback(() => {
    handleNext(true);
  }, [handleNext]);

  switch (question.dbId) {
    case NARRATIVE_STORY_QUESTION_ID: {
      const storyValue = parseStoryValue(value);
      return (
        <NarrativeStory
          {...storyValue}
          ref={questionRef}
          isThirdParty={isThirdParty}
          hideBottomBar={hideBottomBar}
          reportId={reportId}
          handleNext={onNext}
          handlePrevious={onPreviousSection}
          handleStoryChange={handleStoryChange}
        />
      );
    }

    case NARRATIVE_LESSON_QUESTION_ID: {
      const lessonValue = parseLessonValue(value);
      return (
        <NarrativeLesson
          {...lessonValue}
          ref={questionRef}
          isThirdParty={isThirdParty}
          hideBottomBar={hideBottomBar}
          handleNext={handleLessonChange}
          handlePrevious={handleBack}
        />
      );
    }
    default:
      return (
        <>
          <TrueImpactText align="center" color="critical">
            Unable to load question with id of {question.dbId}
          </TrueImpactText>
          {hideBottomBar ? null : (
            <BottomBar
              disabled={false}
              hideBack={false}
              hideNext={true}
              handleBack={handleBack}
              handleNext={() => null}
              backTitle="Back"
              nextTitle="Save & Next Section"
            />
          )}
        </>
      );
  }
};

export default Narrative;
